export interface WizardTimezone {
  value: string;
  label: string;
  offsetMinutes: number;
}

const FALLBACK_TIMEZONES = [
  'UTC',
  'Europe/Madrid',
  'America/New_York',
  'America/Mexico_City',
  'America/Bogota',
];

export class AuthWizardTimezoneCatalog {
  build(): WizardTimezone[] {
    try {
      const supported = this.getSupportedTimezones();
      if (!supported.length) return this.fallback();

      const now = new Date();
      return supported.map((timezone) => {
        const offset = this.buildOffset(timezone, now);
        return {
          value: timezone,
          label: `(${offset.label}) ${timezone}`,
          offsetMinutes: offset.minutes,
        };
      }).sort(
        (left, right) => left.offsetMinutes - right.offsetMinutes
          || left.value.localeCompare(right.value)
      );
    } catch {
      return this.fallback();
    }
  }

  guess(timezones: WizardTimezone[]): string | null {
    try {
      const guessed = Intl.DateTimeFormat().resolvedOptions().timeZone;
      return guessed && timezones.some((item) => item.value === guessed) ? guessed : null;
    } catch {
      return null;
    }
  }

  private getSupportedTimezones(): string[] {
    try {
      const values = (Intl as any).supportedValuesOf('timeZone');
      return Array.isArray(values) ? values : [];
    } catch {
      return [];
    }
  }

  private buildOffset(timezone: string, now: Date): { label: string; minutes: number } {
    try {
      const formatter = new Intl.DateTimeFormat('en-US', {
        timeZone: timezone,
        timeZoneName: 'shortOffset',
        hour: '2-digit',
        minute: '2-digit',
      });
      const name = formatter.formatToParts(now)
        .find((part) => part.type === 'timeZoneName')?.value || '';
      const match = name.match(/GMT([+-]\d{1,2})(?::(\d{2}))?/);
      if (match) {
        const hours = parseInt(match[1], 10);
        const minuteText = match[2] || '00';
        const minutes = hours * 60
          + (hours >= 0 ? 1 : -1) * parseInt(minuteText, 10);
        const sign = hours >= 0 ? '+' : '-';
        return {
          label: `UTC${sign}${String(Math.abs(hours)).padStart(2, '0')}:${minuteText}`,
          minutes,
        };
      }
    } catch {
      // Fall through to the locale-based calculation for older browsers.
    }

    try {
      const utc = Date.UTC(
        now.getUTCFullYear(),
        now.getUTCMonth(),
        now.getUTCDate(),
        now.getUTCHours(),
        now.getUTCMinutes(),
        now.getUTCSeconds()
      );
      const timezoneDate = new Date(now.toLocaleString('en-US', { timeZone: timezone }));
      const minutes = Math.round((timezoneDate.getTime() - utc) / 60_000);
      const sign = minutes >= 0 ? '+' : '-';
      const absolute = Math.abs(minutes);
      return {
        label: `UTC${sign}${String(Math.floor(absolute / 60)).padStart(2, '0')}:${String(absolute % 60).padStart(2, '0')}`,
        minutes,
      };
    } catch {
      return { label: 'UTC+00:00', minutes: 0 };
    }
  }

  private fallback(): WizardTimezone[] {
    return FALLBACK_TIMEZONES.map((timezone) => ({
      value: timezone,
      label: `(UTC) ${timezone}`,
      offsetMinutes: 0,
    }));
  }
}
